import 'components/DSYS/editor/dynamicNode/registerDynamicNodes';

import {Box, ThemeContainer, ThemeProvider} from '@dropbox/dig-foundations';
import {
  BoldLine,
  DropdownLine,
  H1Line,
  H2Line,
  HashtagLine,
  HighlightLine,
  ItalicLine,
  LinkLine,
  OrderedListLine,
  PlaceholderTextLine,
  RedactLine,
  StrikethroughLine,
  TableHeaderColumnLine,
  TableHeaderRowLine,
  ToDoLine,
  UnderlineLine,
  UnorderedListLine,
} from '@dropbox/dig-icons/assets';
import {$createLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND} from '@lexical/link';
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
} from '@lexical/list';
import {$unwrapMarkNode} from '@lexical/mark';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
  $getSelectionStyleValueForProperty,
  $patchStyleText,
  $setBlocksType,
} from '@lexical/selection';
import {
  $computeTableMapSkipCellCheck,
  $getTableNodeFromLexicalNodeOrThrow,
  $isTableCellNode,
  TableCellHeaderStates,
  TableCellNode,
  TableSelection,
} from '@lexical/table';
import {$findMatchingParent, $getNearestNodeOfType, IS_APPLE, mergeRegister} from '@lexical/utils';
import {activeAISuggestionAtom} from 'atoms/aiSuggestion';
import {activeCommentAtom, commentDraftDirtyAtom, type CommentState} from 'atoms/comment';
import {employeeAtom} from 'atoms/employee';
import {derivedThemeAtom} from 'atoms/layout';
import {DocumentRedactionActivateRequest, DocumentService} from 'client';
import {useCollabContext} from 'components/DSYS/CollabProvider';
import {
  $isExcludeWordcountNode,
  $wrapSelectionInExcludeWordcountNode,
} from 'components/DSYS/editor/nodes/ExcludeWordcountNode';
import {$isHighlightNode} from 'components/DSYS/editor/nodes/HighlightNode';
import {
  $isPlaceholderNode,
  $wrapSelectionInPlaceholderNode,
} from 'components/DSYS/editor/nodes/PlaceholderNode';
import {$isRedactionNode} from 'components/DSYS/editor/nodes/RedactionNode';
import {
  $createRedactionV2Node,
  $isRedactionV2Node,
  getRedactionV2MaskSize,
  REDACTION_V2_INLINE_CONTENT_KIND,
  RedactionV2InlineContent,
  RedactionV2MaskSize,
  RedactionV2TextContentNode,
} from 'components/DSYS/editor/nodes/RedactionV2Node';
import {$createTypeaheadNode} from 'components/DSYS/editor/nodes/TypeaheadNode';
import {useThreads} from 'components/DSYS/editor/plugins/comments/useThreads';
import {TOGGLE_REDACTION_V2_EDITOR_COMMAND} from 'components/DSYS/editor/plugins/redactionV2/RedactionV2Context';
import {STRUCTURED_COMMENT_MULTI_BLOCK_CLASS} from 'components/DSYS/editor/structuredCommentSelection';
import {
  findHighlightSpanOwnedByEditor,
  getNestedCommentDisplayPosition,
  getSelectedNode,
  isInsideNestedEditor,
  LowPriority,
} from 'components/DSYS/editor/utils';
import {getPlanningCommentOriginIdFromElement} from 'components/DSYS/editor/utils/planningCommentOrigin';
import {confirmDiscardCommentDraft} from 'helpers/commentDraft';
import {useCommentActions} from 'hooks/useCommentActions';
import {t} from 'i18next';
import {useAtom, useAtomValue} from 'jotai';
import {
  $createParagraphNode,
  $createRangeSelection,
  $createTextNode,
  $getNodeByKey,
  $getSelection,
  $insertNodes,
  $isDecoratorNode,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  CLEAR_HISTORY_COMMAND,
  COMMAND_PRIORITY_HIGH,
  createCommand,
  FORMAT_TEXT_COMMAND,
  IS_HIGHLIGHT,
  KEY_DOWN_COMMAND,
  LexicalEditor,
  LexicalNode,
  RangeSelection,
  SELECTION_CHANGE_COMMAND,
  SerializedTextNode,
  TextNode,
} from 'lexical';
import {
  AI_SUGGESTION_ACCEPTANCE_TAG,
  AISuggestionMarkNode,
} from 'plugins/aiRules/AISuggestionMarkNode';
import {AISuggestionToolbar} from 'plugins/aiRules/AISuggestionToolbar';
import {AISuggestion} from 'plugins/aiRules/types';
import {
  $createEnhancedHeadingNode,
  $isEnhancedHeadingNode,
  useIsTemplateMode,
} from 'plugins/enhancedHeadings';
import {$isEnhancedTableNode} from 'plugins/enhancedTableCells';
import {type ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {getService} from 'utilities';

import commentStyles from './Comment.module.css';
import {getCommentDisplayMaxHeightForAnchor} from './commentDisplaySizing';
import {CommentDisplay} from './comments/CommentDisplay';
import {
  CommentCreatedData,
  CommentToolbar,
  CommentToolbarButton,
} from './comments/CommentToolbarButton';
import {MentionTypeaheadOption} from './MentionPlugin';
import {ColorSelectionToolbar} from './toolbar/ColorSelectionToolbar';
import {DynamicNodeInsertToolbarButton} from './toolbar/DynamicNodeInsertToolbarButton';
import {ImageToolbarButton} from './toolbar/ImageToolbarButton';
import {toggleTableColumnHeaders, toggleTableRowHeaders} from './toolbar/tableHeaderHelpers';
import {TableSelectionInfo, TableToolbar} from './toolbar/TableToolbar';
import {TableToolbarButton} from './toolbar/TableToolbarButton';
import {ToolbarButton} from './toolbar/ToolbarButton';

// Track which toolbar owns rendering an unattached comment thread so only one shows it
let unattachedCommentOwnerEditorKey: string | null = null;
let unattachedCommentOwnerThreadId: string | null = null;

const cssEscape = (value: string): string =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(value)
    : value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

export const getCommentHighlightSelector = (threadId: string): string =>
  `span[data-highlight-id~="${cssEscape(threadId)}"]`;

const hasVisibleClientRect = (element: HTMLElement): boolean =>
  Array.from(element.getClientRects()).some((rect) => rect.width > 0 && rect.height > 0);

export const findPrimaryCommentHighlightElement = (
  threadId: string,
  root: ParentNode = document
): HTMLElement | null => {
  const elements = Array.from(
    root.querySelectorAll<HTMLElement>(getCommentHighlightSelector(threadId))
  );

  return elements.find(hasVisibleClientRect) ?? elements[0] ?? null;
};

const editorOwnsCommentHighlightElement = (
  editorElement: HTMLElement | null,
  highlightElement: HTMLElement
): boolean =>
  Boolean(
    editorElement &&
    editorElement.contains(highlightElement) &&
    !isInsideNestedEditor(highlightElement, editorElement)
  );

export const shouldRenderActiveCommentForHighlight = ({
  activeComment,
  editorElement,
  highlightElement,
  root = document,
}: {
  activeComment: Pick<CommentState, 'threadId' | 'editorKey' | 'planningCommentOriginId'>;
  editorElement: HTMLElement | null;
  highlightElement: HTMLElement | null;
  root?: ParentNode;
}): boolean => {
  if (activeComment.planningCommentOriginId && highlightElement) {
    return (
      getPlanningCommentOriginIdFromElement(highlightElement) ===
      activeComment.planningCommentOriginId
    );
  }

  if (activeComment.editorKey || !highlightElement) {
    return true;
  }

  const primaryHighlightElement = findPrimaryCommentHighlightElement(activeComment.threadId, root);

  return (
    !primaryHighlightElement ||
    editorOwnsCommentHighlightElement(editorElement, primaryHighlightElement)
  );
};

const CommentRailPortal = ({threadId, children}: {threadId: string; children: ReactNode}) => {
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let frameId: number | null = null;
    const selector = `[data-comments-panel-active-thread-id="${cssEscape(threadId)}"]`;

    const updateHost = () => {
      if (frameId !== null) {
        return;
      }

      frameId = requestAnimationFrame(() => {
        frameId = null;
        setHost(document.querySelector<HTMLElement>(selector));
      });
    };

    updateHost();

    const observer = new MutationObserver(updateHost);
    observer.observe(document.body, {childList: true, subtree: true});

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      observer.disconnect();
    };
  }, [threadId]);

  return host ? createPortal(children, host) : null;
};

const COMMENTS_PANEL_COMMENT_BOX_MAX_HEIGHT = '66vh';

/**
 * Helper type to represent word/text segments with their formatting and link info
 */
interface TextSegment {
  text: string;
  format?: number;
  style?: string;
  linkUrl?: string;
  linkTarget?: string | null;
  linkRel?: string | null;
  linkTitle?: string | null;
}

/**
 * Extract text segments with formatting and link info from nodes
 */
const extractTextSegments = (nodes: LexicalNode[]): TextSegment[] => {
  const segments: TextSegment[] = [];

  for (const node of nodes) {
    if ($isLinkNode(node)) {
      const linkUrl = node.getURL();
      const linkTarget = node.getTarget();
      const linkRel = node.getRel();
      const linkTitle = node.getTitle();

      const children = node.getChildren();
      for (const child of children) {
        if ($isTextNode(child)) {
          segments.push({
            text: child.getTextContent(),
            format: child.getFormat(),
            style: child.getStyle(),
            linkUrl,
            linkTarget,
            linkRel,
            linkTitle,
          });
        }
      }
    } else if ($isTextNode(node)) {
      const parent = node.getParent();
      if ($isLinkNode(parent)) {
        segments.push({
          text: node.getTextContent(),
          format: node.getFormat(),
          style: node.getStyle(),
          linkUrl: parent.getURL(),
          linkTarget: parent.getTarget(),
          linkRel: parent.getRel(),
          linkTitle: parent.getTitle(),
        });
      } else {
        segments.push({
          text: node.getTextContent(),
          format: node.getFormat(),
          style: node.getStyle(),
        });
      }
    }
  }

  return segments;
};

/**
 * Helper function to recreate formatted text nodes while preserving formatting
 */
const recreateFormattedNodes = (
  originalNodes: LexicalNode[],
  suggestedText: string
): LexicalNode[] => {
  const originalSegments = extractTextSegments(originalNodes);
  const originalText = originalSegments.map((seg) => seg.text).join('');

  if (suggestedText === originalText) {
    const newNodes: LexicalNode[] = [];
    for (const segment of originalSegments) {
      const textNode = $createTextNode(segment.text);
      if (segment.format !== undefined) textNode.setFormat(segment.format);
      if (segment.style) textNode.setStyle(segment.style);

      if (segment.linkUrl) {
        const linkNode = $createLinkNode(segment.linkUrl, {
          target: segment.linkTarget,
          rel: segment.linkRel,
          title: segment.linkTitle,
        });
        linkNode.append(textNode);
        newNodes.push(linkNode);
      } else {
        newNodes.push(textNode);
      }
    }
    return newNodes;
  }

  const originalWords = originalText.split(/(\s+)/);
  const suggestedWords = suggestedText.split(/(\s+)/);

  const wordToSegment = new Map<number, TextSegment>();
  let currentPos = 0;
  let currentSegmentIndex = 0;
  let segmentOffset = 0;

  for (let i = 0; i < originalWords.length; i++) {
    const word = originalWords[i];
    while (currentSegmentIndex < originalSegments.length) {
      const segment = originalSegments[currentSegmentIndex];
      const segmentEnd = segmentOffset + segment.text.length;
      if (currentPos < segmentEnd) {
        wordToSegment.set(i, segment);
        break;
      }
      segmentOffset = segmentEnd;
      currentSegmentIndex++;
    }
    currentPos += word.length;
  }

  const newNodes: LexicalNode[] = [];
  let currentText = '';
  let currentSegment: TextSegment | null = null;

  const flushCurrentText = () => {
    if (currentText && currentSegment) {
      const textNode = $createTextNode(currentText);
      if (currentSegment.format !== undefined) textNode.setFormat(currentSegment.format);
      if (currentSegment.style) textNode.setStyle(currentSegment.style);

      if (currentSegment.linkUrl) {
        const linkNode = $createLinkNode(currentSegment.linkUrl, {
          target: currentSegment.linkTarget,
          rel: currentSegment.linkRel,
          title: currentSegment.linkTitle,
        });
        linkNode.append(textNode);
        newNodes.push(linkNode);
      } else {
        newNodes.push(textNode);
      }

      currentText = '';
      currentSegment = null;
    } else if (currentText) {
      newNodes.push($createTextNode(currentText));
      currentText = '';
    }
  };

  for (let i = 0; i < suggestedWords.length; i++) {
    const suggestedWord = suggestedWords[i];
    const originalWordIndex = originalWords.indexOf(suggestedWord);
    const matchedSegment = originalWordIndex >= 0 ? wordToSegment.get(originalWordIndex) : null;

    if (matchedSegment) {
      const segmentsMatch =
        currentSegment &&
        currentSegment.format === matchedSegment.format &&
        currentSegment.style === matchedSegment.style &&
        currentSegment.linkUrl === matchedSegment.linkUrl;

      if (!segmentsMatch) {
        flushCurrentText();
        currentSegment = matchedSegment;
      }

      currentText += suggestedWord;
    } else {
      flushCurrentText();
      currentText += suggestedWord;
    }
  }

  flushCurrentText();

  if (newNodes.length === 0) {
    return [$createTextNode(suggestedText)];
  }

  return newNodes;
};

type SelectionAnalysis = {
  hasDecoratorNode: boolean;
  hasRedactionNode: boolean;
  hasTableContent: boolean;
  hasNonTableContent: boolean;
  tableCellCount: number;
  contentfulTopLevelBlockCount: number;
};

export const deriveSelectionCapabilities = ({
  hasDecoratorNode,
  hasRedactionNode,
  hasTableContent,
  hasNonTableContent,
  tableCellCount,
  contentfulTopLevelBlockCount,
}: SelectionAnalysis) => {
  const spansMultipleTableCellsOrTableBoundary =
    hasTableContent && (tableCellCount > 1 || hasNonTableContent);

  const isBlockTransformable =
    !hasDecoratorNode && !hasRedactionNode && !spansMultipleTableCellsOrTableBoundary;

  const isWrappable =
    isBlockTransformable && (hasTableContent || contentfulTopLevelBlockCount <= 1);

  return {
    isBlockTransformable,
    isWrappable,
  };
};

export const deriveRedactionCapabilities = ({
  hasDecoratorNode,
  hasRedactionNode,
  hasTableContent,
  hasNonTableContent,
  tableCellCount,
}: SelectionAnalysis) => {
  const spansMultipleTableCellsOrTableBoundary =
    hasTableContent && (tableCellCount > 1 || hasNonTableContent);

  return {
    isRedactable: !hasDecoratorNode && !hasRedactionNode && !spansMultipleTableCellsOrTableBoundary,
  };
};

export const createRedactionV2Id = (
  crypto: Crypto | null | undefined = globalThis.crypto
): string | null => {
  if (typeof crypto?.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  if (typeof crypto?.getRandomValues !== 'function') {
    return null;
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
};

export const shouldAllowMultiBlockCommentWrapping = (
  editorRoot: HTMLElement | null,
  range: Range | null
) => {
  if (!editorRoot || !range) {
    return false;
  }

  const getStructuredContainer = (node: Node) => {
    const element = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
    const container = element?.closest?.(`.${STRUCTURED_COMMENT_MULTI_BLOCK_CLASS}`);
    return container instanceof HTMLElement && editorRoot.contains(container) ? container : null;
  };

  const startContainer = getStructuredContainer(range.startContainer);
  const endContainer = getStructuredContainer(range.endContainer);

  return startContainer !== null && startContainer === endContainer;
};

type RedactionV2SelectionSnapshot = {
  anchor: RedactionV2SelectionPoint;
  focus: RedactionV2SelectionPoint;
  text: string;
  content: RedactionV2InlineContent;
  redactionId: string;
  maskSize: RedactionV2MaskSize;
};

type RedactionV2SelectionPoint = {
  key: string;
  offset: number;
};

type RedactionV2SelectionSnapshotResult = {
  snapshot: RedactionV2SelectionSnapshot | null;
  reason: string;
  details?: Record<string, unknown>;
};

type RedactionV2CommentThreadIds = ReadonlySet<string> | null | undefined;

type RedactionV2SelectionSnapshotOptions = {
  commentThreadIds?: RedactionV2CommentThreadIds;
};

type RedactionV2ThreadLike = {
  id: string;
  comments?: Array<{is_deleted?: boolean | null}> | null;
};

export const shouldRestoreSelectedTextToolbarAfterRedactionV2Create = ({
  isCollapsedSelection,
  isRedactionSelection,
  wasHiddenForRedactionV2Create,
}: {
  isCollapsedSelection: boolean;
  isRedactionSelection: boolean;
  wasHiddenForRedactionV2Create: boolean;
}): boolean => wasHiddenForRedactionV2Create && !isCollapsedSelection && !isRedactionSelection;

const MAX_REDACTION_V2_TEXT_LENGTH = 5000;
const REDACTION_V2_PERSISTENCE_RETRY_COUNT = 20;
const REDACTION_V2_PERSISTENCE_RETRY_DELAY_MS = 500;
const TEXT_FORMAT_BOLD = 1;
const TEXT_FORMAT_ITALIC = 2;
const TEXT_FORMAT_STRIKETHROUGH = 4;
const TEXT_FORMAT_UNDERLINE = 8;
const REDACTION_V2_ACTIVATE_MASK_SIZE: Record<
  RedactionV2MaskSize,
  DocumentRedactionActivateRequest.mask_size
> = {
  short: DocumentRedactionActivateRequest.mask_size.SHORT,
  medium: DocumentRedactionActivateRequest.mask_size.MEDIUM,
  long: DocumentRedactionActivateRequest.mask_size.LONG,
};

export const getDefaultRedactionV2AllowedEmployeeIds = (
  currentEmployee: {user_id?: string | null} | null | undefined,
  collabOwner: {user_id?: string | null} | null | undefined
): string[] => {
  const ids = [currentEmployee?.user_id, collabOwner?.user_id].filter((id): id is string =>
    Boolean(id)
  );
  return Array.from(new Set(ids));
};

const getRedactionV2CommentThreadIds = (
  threads: RedactionV2ThreadLike[] | undefined
): ReadonlySet<string> | null => {
  if (!threads) {
    return null;
  }

  return new Set(
    threads
      .filter((thread) => {
        if (!thread.comments) {
          return true;
        }
        return thread.comments.some((comment) => !comment.is_deleted);
      })
      .map((thread) => thread.id)
  );
};

const redactionV2HighlightIdsIncludeCommentThread = (
  highlightIds: string[],
  commentThreadIds: RedactionV2CommentThreadIds
): boolean => {
  if (highlightIds.length === 0) {
    return false;
  }
  if (!commentThreadIds) {
    return true;
  }
  return highlightIds.some((id) => commentThreadIds.has(id));
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isRedactionV2DebugEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    window.localStorage.getItem('debugRedactionV2') === '1' ||
    new URLSearchParams(window.location.search).has('debugRedactionV2')
  );
};

const debugRedactionV2Selection = (event: string, payload: Record<string, unknown>): void => {
  if (!isRedactionV2DebugEnabled()) {
    return;
  }

  const snapshotDetails = payload.snapshotDetails as Record<string, unknown> | undefined;
  const ancestor = snapshotDetails?.ancestor as Record<string, unknown> | undefined;
  const redactionButtonBlockers = payload.redactionButtonBlockers;
  const blockerSummary = Array.isArray(redactionButtonBlockers)
    ? ` blockers=${redactionButtonBlockers.join('|') || 'none'}`
    : '';
  const summary =
    'snapshotReason' in payload
      ? ` ${String(payload.snapshotReason)}${
          ancestor?.type ? ` ancestor=${String(ancestor.type)}` : ''
        } canUse=${String(payload.canUseRedactionButton)}${blockerSummary}`
      : 'reason' in payload
        ? ` ${String(payload.reason)}${blockerSummary}`
        : 'canShowRedactionControl' in payload
          ? ` canShow=${String(payload.canShowRedactionControl)} canUse=${String(
              payload.canUseRedactionButton
            )}${blockerSummary}`
          : '';
  console.info(`[redaction-v2-selection] ${event}${summary}`, payload);
};

const compactDebugReasons = (reasons: Array<string | false | null | undefined>): string[] =>
  reasons.filter((reason): reason is string => Boolean(reason));

const describeRedactionV2Node = (node: LexicalNode): Record<string, unknown> => ({
  key: node.getKey(),
  type: node.getType(),
  text: node.getTextContent().slice(0, 120),
  parentKey: node.getParent()?.getKey(),
  parentType: node.getParent()?.getType(),
  ...($isHighlightNode(node)
    ? {
        highlightIds: node.getHighlightIds(),
      }
    : {}),
  ...(node instanceof TextNode
    ? {
        detail: node.getDetail(),
        format: node.getFormat(),
        mode: node.getMode(),
        style: node.getStyle(),
      }
    : {}),
});

const getActiveDomSelectionInEditor = (editorRoot: HTMLElement | null): Selection | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const domSelection = window.getSelection();
  if (
    !editorRoot ||
    !domSelection ||
    domSelection.rangeCount === 0 ||
    domSelection.isCollapsed ||
    !domSelection.anchorNode ||
    !domSelection.focusNode ||
    !editorRoot.contains(domSelection.anchorNode) ||
    !editorRoot.contains(domSelection.focusNode) ||
    !editorRoot.contains(domSelection.getRangeAt(0).commonAncestorContainer) ||
    !domSelection.toString().trim()
  ) {
    return null;
  }

  return domSelection;
};

const getDisallowedRedactionV2Ancestor = (
  node: LexicalNode,
  options?: RedactionV2SelectionSnapshotOptions
): LexicalNode | null => {
  let current: LexicalNode | null = node;
  while (current) {
    if (
      $isLinkNode(current) ||
      $isRedactionNode(current) ||
      $isRedactionV2Node(current) ||
      ($isHighlightNode(current) &&
        redactionV2HighlightIdsIncludeCommentThread(
          current.getHighlightIds(),
          options?.commentThreadIds
        ))
    ) {
      return current;
    }
    current = current.getParent();
  }
  return null;
};

const getRedactionV2FormatFromStyle = (style: string): number => {
  const declarations = style
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean);

  let format = 0;
  for (const declaration of declarations) {
    const separatorIndex = declaration.indexOf(':');
    if (separatorIndex <= 0) {
      continue;
    }

    const property = declaration.slice(0, separatorIndex).trim().toLowerCase();
    const value = declaration
      .slice(separatorIndex + 1)
      .trim()
      .toLowerCase();
    if (!value) {
      continue;
    }

    if (property === 'font-weight') {
      const numericWeight = Number.parseInt(value, 10);
      if (
        value === 'bold' ||
        value === 'bolder' ||
        (!Number.isNaN(numericWeight) && numericWeight >= 600)
      ) {
        format |= TEXT_FORMAT_BOLD;
      }
      continue;
    }

    if (property === 'font-style') {
      if (value === 'italic' || value === 'oblique') {
        format |= TEXT_FORMAT_ITALIC;
      }
      continue;
    }

    if (property === 'text-decoration' || property === 'text-decoration-line') {
      if (value === 'none') {
        continue;
      }
      if (value.includes('underline')) {
        format |= TEXT_FORMAT_UNDERLINE;
      }
      if (value.includes('line-through')) {
        format |= TEXT_FORMAT_STRIKETHROUGH;
      }
      continue;
    }
  }

  return format;
};

const restoreRedactionV2Selection = (snapshot: RedactionV2SelectionSnapshot): void => {
  const selection = $createRangeSelection();
  selection.anchor.set(snapshot.anchor.key, snapshot.anchor.offset, 'text');
  selection.focus.set(snapshot.focus.key, snapshot.focus.offset, 'text');
  $setSelection(selection);
};

const hasRedactionV2DisallowedHighlightStyle = (style: string): boolean =>
  style
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .some((declaration) => {
      const separatorIndex = declaration.indexOf(':');
      if (separatorIndex <= 0) {
        return false;
      }

      const property = declaration.slice(0, separatorIndex).trim().toLowerCase();
      const value = declaration
        .slice(separatorIndex + 1)
        .trim()
        .toLowerCase();
      return (
        (property === 'background' || property === 'background-color') &&
        value !== 'transparent' &&
        value !== 'none' &&
        value !== 'inherit' &&
        value !== 'initial' &&
        value !== 'unset'
      );
    });

const getRedactionV2TextNodeFormat = (textNode: TextNode): number =>
  textNode.getFormat() | getRedactionV2FormatFromStyle(textNode.getStyle());

const getRedactionV2BlockSignature = (textNode: TextNode): string => {
  let current: LexicalNode | null = textNode.getParent();

  while (current && current.getKey() !== 'root') {
    if ($isElementNode(current) && !current.isInline()) {
      return `${current.getType()}:${current.getKey()}`;
    }
    current = current.getParent();
  }

  return '';
};

const getSelectedRedactionV2TextSlice = (
  textNode: TextNode,
  startPoint: RangeSelection['anchor'],
  endPoint: RangeSelection['focus']
) => {
  const startOffset =
    startPoint.type === 'text' && textNode.is(startPoint.getNode()) ? startPoint.offset : 0;
  const endOffset =
    endPoint.type === 'text' && textNode.is(endPoint.getNode())
      ? endPoint.offset
      : textNode.getTextContentSize();

  return {
    endOffset,
    node: textNode,
    startOffset,
    text: startOffset < endOffset ? textNode.getTextContent().slice(startOffset, endOffset) : '',
  };
};

type RedactionV2TextSlice = ReturnType<typeof getSelectedRedactionV2TextSlice>;

const areRedactionV2InlineContentNodesMergeable = (
  left: RedactionV2TextContentNode,
  right: RedactionV2TextContentNode
): boolean =>
  left.type === right.type &&
  left.format === right.format &&
  left.detail === right.detail &&
  left.mode === right.mode &&
  left.style === right.style &&
  left.version === right.version;

const getRedactionV2ContentNodeFromTextSlice = (
  slice: RedactionV2TextSlice
): RedactionV2TextContentNode | null => {
  if (!slice.text) {
    return null;
  }

  const serializedNode = slice.node.exportJSON() as SerializedTextNode;
  return {
    type: 'text',
    text: slice.text,
    format: getRedactionV2TextNodeFormat(slice.node),
    detail: serializedNode.detail,
    mode: 'normal',
    style: '',
    version: serializedNode.version,
  };
};

const getRedactionV2ContentNodesFromTextSlices = (
  slices: RedactionV2TextSlice[]
): RedactionV2TextContentNode[] => {
  const nodes: RedactionV2TextContentNode[] = [];

  for (const slice of slices) {
    const contentNode = getRedactionV2ContentNodeFromTextSlice(slice);
    if (!contentNode) {
      continue;
    }

    const previousNode = nodes[nodes.length - 1];
    if (previousNode && areRedactionV2InlineContentNodesMergeable(previousNode, contentNode)) {
      previousNode.text += contentNode.text;
      continue;
    }

    nodes.push(contentNode);
  }

  return nodes;
};

const getRedactionV2SelectionSnapshotResult = (
  selection: RangeSelection,
  options?: RedactionV2SelectionSnapshotOptions
): RedactionV2SelectionSnapshotResult => {
  if (selection.isCollapsed()) {
    return {snapshot: null, reason: 'selection-collapsed'};
  }

  const startPoint = selection.isBackward() ? selection.focus : selection.anchor;
  const endPoint = selection.isBackward() ? selection.anchor : selection.focus;
  const selectedNodes = selection.getNodes();
  const unsupportedSelectedNode = selectedNodes.find(
    (node) =>
      $isDecoratorNode(node) ||
      $isRedactionNode(node) ||
      $isRedactionV2Node(node) ||
      ($isHighlightNode(node) &&
        redactionV2HighlightIdsIncludeCommentThread(
          node.getHighlightIds(),
          options?.commentThreadIds
        )) ||
      (!$isTextNode(node) && !$isElementNode(node))
  );
  if (unsupportedSelectedNode) {
    return {
      snapshot: null,
      reason: 'unsupported-selected-node',
      details: {
        node: describeRedactionV2Node(unsupportedSelectedNode),
        nodes: selectedNodes.map(describeRedactionV2Node),
      },
    };
  }

  const selectedTextNodes = selectedNodes.filter($isTextNode);
  if (selectedTextNodes.length === 0) {
    return {
      snapshot: null,
      reason: 'no-selected-text-nodes',
      details: {nodes: selectedNodes.map(describeRedactionV2Node)},
    };
  }
  const firstTextNode = selectedTextNodes[0];
  const blockSignature = getRedactionV2BlockSignature(firstTextNode);

  for (const textNode of selectedTextNodes) {
    const disallowedAncestor = getDisallowedRedactionV2Ancestor(textNode, options);
    if (disallowedAncestor) {
      return {
        snapshot: null,
        reason: 'unsupported-ancestor',
        details: {
          node: describeRedactionV2Node(textNode),
          ancestor: describeRedactionV2Node(disallowedAncestor),
        },
      };
    }

    if (hasRedactionV2DisallowedHighlightStyle(textNode.getStyle())) {
      return {
        snapshot: null,
        reason: 'unsupported-highlight-style',
        details: {node: describeRedactionV2Node(textNode)},
      };
    }

    if ((textNode.getFormat() & IS_HIGHLIGHT) !== 0) {
      return {
        snapshot: null,
        reason: 'unsupported-highlight-format',
        details: {node: describeRedactionV2Node(textNode)},
      };
    }

    if (getRedactionV2BlockSignature(textNode) !== blockSignature) {
      return {
        snapshot: null,
        reason: 'selection-spans-multiple-blocks',
        details: {
          nodes: selectedTextNodes.map(describeRedactionV2Node),
          blockSignature,
        },
      };
    }
  }

  const textSlices = selectedTextNodes.map((textNode) =>
    getSelectedRedactionV2TextSlice(textNode, startPoint, endPoint)
  );
  const text = textSlices.map((slice) => slice.text).join('');

  if (!text.trim()) {
    return {snapshot: null, reason: 'selected-text-empty-after-trim', details: {text}};
  }
  if (text.length > MAX_REDACTION_V2_TEXT_LENGTH) {
    return {
      snapshot: null,
      reason: 'selected-text-too-long',
      details: {length: text.length, maxLength: MAX_REDACTION_V2_TEXT_LENGTH},
    };
  }
  const redactionId = createRedactionV2Id();
  if (!redactionId) {
    return {snapshot: null, reason: 'no-uuid-generator'};
  }

  const firstSlice = textSlices[0];
  const lastSlice = textSlices[textSlices.length - 1];
  const contentNodes = getRedactionV2ContentNodesFromTextSlices(textSlices);

  return {
    snapshot: {
      anchor: selection.isBackward()
        ? {
            key: lastSlice.node.getKey(),
            offset: lastSlice.endOffset,
          }
        : {
            key: firstSlice.node.getKey(),
            offset: firstSlice.startOffset,
          },
      focus: selection.isBackward()
        ? {
            key: firstSlice.node.getKey(),
            offset: firstSlice.startOffset,
          }
        : {
            key: lastSlice.node.getKey(),
            offset: lastSlice.endOffset,
          },
      text,
      content: {
        kind: REDACTION_V2_INLINE_CONTENT_KIND,
        nodes: contentNodes,
      },
      redactionId,
      maskSize: getRedactionV2MaskSize(text.length),
    },
    reason: 'eligible',
    details: {
      text,
      nodes: selectedTextNodes.map(describeRedactionV2Node),
      contentNodes,
      blockSignature,
    },
  };
};

type RedactionV2DomSelectionPoint = {
  domNode: Node;
  node: TextNode;
  offset: number;
};

const describeRedactionV2DomNode = (node: Node | null): Record<string, unknown> | null => {
  if (!node) {
    return null;
  }

  return {
    nodeName: node.nodeName,
    nodeType: node.nodeType,
    text: node.textContent?.slice(0, 120) ?? '',
  };
};

const getElementForRedactionV2DomNode = (node: Node | null): Element | null => {
  if (!node) {
    return null;
  }
  return node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
};

const getRedactionV2HighlightIdsForDomElement = (element: Element): string[] =>
  element.getAttribute('data-highlight-id')?.split(/\s+/).filter(Boolean) ?? [];

const isRedactionV2CommentHighlightElement = (
  element: Element,
  options?: RedactionV2SelectionSnapshotOptions
): boolean =>
  element.matches('span[data-highlight-id]') &&
  redactionV2HighlightIdsIncludeCommentThread(
    getRedactionV2HighlightIdsForDomElement(element),
    options?.commentThreadIds
  );

const hasRedactionV2CommentHighlightAncestor = (
  node: Node | null,
  options?: RedactionV2SelectionSnapshotOptions
): boolean => {
  let current = getElementForRedactionV2DomNode(node);
  while (current) {
    if (isRedactionV2CommentHighlightElement(current, options)) {
      return true;
    }
    current = current.parentElement;
  }
  return false;
};

export const redactionV2DomRangeIntersectsCommentHighlight = (
  range: Range,
  options?: RedactionV2SelectionSnapshotOptions
): boolean => {
  if (
    hasRedactionV2CommentHighlightAncestor(range.startContainer, options) ||
    hasRedactionV2CommentHighlightAncestor(range.endContainer, options)
  ) {
    return true;
  }

  const commonAncestorElement = getElementForRedactionV2DomNode(range.commonAncestorContainer);
  if (!commonAncestorElement) {
    return false;
  }

  return Array.from(commonAncestorElement.querySelectorAll('span[data-highlight-id]')).some(
    (element) => {
      if (!isRedactionV2CommentHighlightElement(element, options)) {
        return false;
      }
      try {
        return range.intersectsNode(element);
      } catch {
        return false;
      }
    }
  );
};

const getNearestLexicalNodeForDomNode = (
  domNode: Node,
  editor: LexicalEditor
): LexicalNode | null => {
  const editorKey = (editor as unknown as {_key?: string})._key;
  if (!editorKey) {
    return null;
  }

  let current: Node | null = domNode;
  while (current) {
    const key = (current as unknown as Record<string, string | undefined>)[
      `__lexicalKey_${editorKey}`
    ];
    if (key) {
      return $getNodeByKey(key);
    }
    current = current.parentNode;
  }

  return null;
};

const getLexicalTextNodeForDomNode = (domNode: Node, editor: LexicalEditor): TextNode | null => {
  const lexicalNode = getNearestLexicalNodeForDomNode(domNode, editor);
  return $isTextNode(lexicalNode) ? lexicalNode : null;
};

const getFirstMappedDomTextNode = (node: Node | null, editor: LexicalEditor): Text | null => {
  if (!node) {
    return null;
  }

  if (node.nodeType === Node.TEXT_NODE && getLexicalTextNodeForDomNode(node, editor)) {
    return node as Text;
  }

  for (const child of Array.from(node.childNodes)) {
    const textNode = getFirstMappedDomTextNode(child, editor);
    if (textNode) {
      return textNode;
    }
  }

  return null;
};

const getLastMappedDomTextNode = (node: Node | null, editor: LexicalEditor): Text | null => {
  if (!node) {
    return null;
  }

  if (node.nodeType === Node.TEXT_NODE && getLexicalTextNodeForDomNode(node, editor)) {
    return node as Text;
  }

  const children = Array.from(node.childNodes);
  for (let index = children.length - 1; index >= 0; index -= 1) {
    const textNode = getLastMappedDomTextNode(children[index], editor);
    if (textNode) {
      return textNode;
    }
  }

  return null;
};

const resolveRedactionV2DomRangePoint = (
  container: Node,
  offset: number,
  edge: 'start' | 'end',
  editor: LexicalEditor
): RedactionV2DomSelectionPoint | null => {
  if (container.nodeType === Node.TEXT_NODE) {
    const textNode = getLexicalTextNodeForDomNode(container, editor);
    if (!textNode) {
      return null;
    }

    return {
      domNode: container,
      node: textNode,
      offset: Math.max(0, Math.min(offset, textNode.getTextContentSize())),
    };
  }

  const children = Array.from(container.childNodes);
  if (edge === 'start') {
    for (let index = offset; index < children.length; index += 1) {
      const domNode = getFirstMappedDomTextNode(children[index], editor);
      const textNode = domNode ? getLexicalTextNodeForDomNode(domNode, editor) : null;
      if (domNode && textNode) {
        return {domNode, node: textNode, offset: 0};
      }
    }
  } else {
    for (let index = offset - 1; index >= 0; index -= 1) {
      const domNode = getLastMappedDomTextNode(children[index], editor);
      const textNode = domNode ? getLexicalTextNodeForDomNode(domNode, editor) : null;
      if (domNode && textNode) {
        return {domNode, node: textNode, offset: textNode.getTextContentSize()};
      }
    }
  }

  return null;
};

const normalizeRedactionV2SelectedTextForComparison = (text: string): string =>
  text
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getRedactionV2SelectionSnapshotResultFromDomSelection = (
  domSelection: Selection,
  editor: LexicalEditor,
  options?: RedactionV2SelectionSnapshotOptions
): RedactionV2SelectionSnapshotResult => {
  const domSelectionText = domSelection.toString().trim();
  try {
    if (domSelection.rangeCount === 0) {
      return {
        snapshot: null,
        reason: 'dom-selection-has-no-range',
        details: {domSelectionText},
      };
    }

    const range = domSelection.getRangeAt(0);
    if (redactionV2DomRangeIntersectsCommentHighlight(range, options)) {
      return {
        snapshot: null,
        reason: 'dom-selection-includes-comment-highlight',
        details: {domSelectionText},
      };
    }

    const start = resolveRedactionV2DomRangePoint(
      range.startContainer,
      range.startOffset,
      'start',
      editor
    );
    const end = resolveRedactionV2DomRangePoint(range.endContainer, range.endOffset, 'end', editor);
    if (!start || !end) {
      return {
        snapshot: null,
        reason: 'dom-selection-not-convertible',
        details: {
          domSelectionText,
          endContainer: describeRedactionV2DomNode(range.endContainer),
          endOffset: range.endOffset,
          resolvedEnd: end
            ? {
                domNode: describeRedactionV2DomNode(end.domNode),
                node: describeRedactionV2Node(end.node),
                offset: end.offset,
              }
            : null,
          resolvedStart: start
            ? {
                domNode: describeRedactionV2DomNode(start.domNode),
                node: describeRedactionV2Node(start.node),
                offset: start.offset,
              }
            : null,
          startContainer: describeRedactionV2DomNode(range.startContainer),
          startOffset: range.startOffset,
        },
      };
    }

    const selection = $createRangeSelection();
    selection.anchor.set(start.node.getKey(), start.offset, 'text');
    selection.focus.set(end.node.getKey(), end.offset, 'text');
    const result = getRedactionV2SelectionSnapshotResult(selection, options);
    if (
      result.snapshot &&
      normalizeRedactionV2SelectedTextForComparison(result.snapshot.text) !==
        normalizeRedactionV2SelectedTextForComparison(domSelectionText)
    ) {
      return {
        snapshot: null,
        reason: 'dom-resolved-text-mismatch',
        details: {
          domSelectionText,
          resolvedSelectionText: result.snapshot.text,
          resolvedEnd: {
            domNode: describeRedactionV2DomNode(end.domNode),
            node: describeRedactionV2Node(end.node),
            offset: end.offset,
          },
          resolvedStart: {
            domNode: describeRedactionV2DomNode(start.domNode),
            node: describeRedactionV2Node(start.node),
            offset: start.offset,
          },
        },
      };
    }

    return {
      ...result,
      reason: `dom-resolved-${result.reason}`,
      details: {
        ...result.details,
        domSelectionText,
        resolvedEnd: {
          domNode: describeRedactionV2DomNode(end.domNode),
          node: describeRedactionV2Node(end.node),
          offset: end.offset,
        },
        resolvedStart: {
          domNode: describeRedactionV2DomNode(start.domNode),
          node: describeRedactionV2Node(start.node),
          offset: start.offset,
        },
      },
    };
  } catch (error) {
    return {
      snapshot: null,
      reason: 'dom-selection-conversion-error',
      details: {
        domSelectionText,
        error: error instanceof Error ? error.message : String(error),
      },
    };
  }
};

export const getRedactionV2SelectionSnapshot = (
  selection: RangeSelection,
  options?: RedactionV2SelectionSnapshotOptions
): RedactionV2SelectionSnapshot | null =>
  getRedactionV2SelectionSnapshotResult(selection, options).snapshot;

const activateRedactionV2WithRetry = async ({
  documentId,
  redactionId,
  maskSize,
}: {
  documentId: string;
  redactionId: string;
  maskSize: RedactionV2MaskSize;
}) => {
  let lastError: unknown;
  for (let attempt = 0; attempt < REDACTION_V2_PERSISTENCE_RETRY_COUNT; attempt += 1) {
    try {
      return await getService(
        DocumentService
      ).activateRedactionDraftApiV1DocumentDocumentIdRedactionsRedactionIdActivatePost(
        documentId,
        redactionId,
        {mask_size: REDACTION_V2_ACTIVATE_MASK_SIZE[maskSize]}
      );
    } catch (error) {
      lastError = error;
      await delay(REDACTION_V2_PERSISTENCE_RETRY_DELAY_MS);
    }
  }
  throw lastError;
};

const getSelectionAnalysis = (selection: RangeSelection): SelectionAnalysis => {
  if (selection.isCollapsed()) {
    return {
      hasDecoratorNode: false,
      hasRedactionNode: false,
      hasTableContent: false,
      hasNonTableContent: false,
      tableCellCount: 0,
      contentfulTopLevelBlockCount: 0,
    };
  }

  const nodes = selection.getNodes();
  if (nodes.length === 0) {
    return {
      hasDecoratorNode: false,
      hasRedactionNode: false,
      hasTableContent: false,
      hasNonTableContent: false,
      tableCellCount: 0,
      contentfulTopLevelBlockCount: 0,
    };
  }

  // Track top-level blocks that contain actual selected text content.
  // This avoids treating boundary-only nodes (e.g. paragraph-end selection artifacts) as multi-block.
  const contentfulTopLevelBlocks = new Set<string>();
  let hasDecoratorNode = false;
  let hasRedactionNode = false;
  const tableCells = new Set<string>();
  let hasTableContent = false;
  let hasNonTableContent = false;

  for (const node of nodes) {
    // Check for decorator nodes (images, videos, mentions, etc.) - can't wrap these
    if ($isDecoratorNode(node)) {
      hasDecoratorNode = true;
      break;
    }

    // Check for redaction nodes - can't comment on redacted content
    if ($isRedactionNode(node) || $isRedactionV2Node(node)) {
      hasRedactionNode = true;
      break;
    }

    // Check if node is inside a redaction
    const redactionParent = $findMatchingParent(node, $isRedactionNode);
    const redactionV2Parent = $findMatchingParent(node, $isRedactionV2Node);
    if (redactionParent || redactionV2Parent) {
      hasRedactionNode = true;
      break;
    }

    // Check if node is inside a table cell
    const tableCell = $findMatchingParent(node, $isTableCellNode);
    if (tableCell) {
      hasTableContent = true;
      tableCells.add(tableCell.getKey());
    } else {
      hasNonTableContent = true;
    }

    // Get the top-level block element (paragraph, heading, list item, etc.)
    let topLevel = node;
    while (topLevel.getParent() && topLevel.getParent()?.getKey() !== 'root') {
      const parent = topLevel.getParent();
      if (!parent) break;

      // Stop at table cell boundary - we want to track blocks within cells
      if ($isTableCellNode(parent)) {
        break;
      }

      topLevel = parent;
    }

    // Track unique top-level blocks with meaningful selected content.
    if (node.getTextContent().trim().length > 0) {
      contentfulTopLevelBlocks.add(topLevel.getKey());
    }
  }

  return {
    hasDecoratorNode,
    hasRedactionNode,
    hasTableContent,
    hasNonTableContent,
    tableCellCount: tableCells.size,
    contentfulTopLevelBlockCount: contentfulTopLevelBlocks.size,
  };
};

type SelectedTextToolbarPayload = {open: boolean};
export const TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND = createCommand<SelectedTextToolbarPayload>();

export type SelectedTextControls =
  | 'styling'
  | 'highlighting'
  | 'enhancements'
  | 'insertions'
  | 'headers'
  | 'templates'
  | 'ai'
  | 'comments'
  | 'table';

type SelectedTextToolbarProps = {
  controls: SelectedTextControls[];
  isSelectionActive: boolean;
  setIsSelectionActive: (isSelectionActive: boolean) => void;
  isMouseDown: boolean;
  tableSelection?: TableSelection | null;
  placement?: string;
  floatingReferenceRect?: DOMRect | null;
  activeCommentInputAutoFocus?: boolean;
  commentMentions?: MentionTypeaheadOption[];
  disableAISuggestions?: boolean;
};

export const shouldHideUnsupportedCommentOnlyToolbar = ({
  controls,
  isSelectionActive,
  isSelectionWrappable,
}: Pick<SelectedTextToolbarProps, 'controls' | 'isSelectionActive'> & {
  isSelectionWrappable: boolean;
}) =>
  isSelectionActive && !isSelectionWrappable && controls.length === 1 && controls[0] === 'comments';

export const SelectedTextToolbar = ({
  controls,
  isSelectionActive,
  setIsSelectionActive,
  isMouseDown,
  tableSelection,
  placement,
  floatingReferenceRect,
  activeCommentInputAutoFocus = true,
  commentMentions,
  disableAISuggestions = false,
}: SelectedTextToolbarProps) => {
  const [editor] = useLexicalComposerContext();
  const isTemplateMode = useIsTemplateMode();
  const [isVisible, setIsVisible] = useState(true);
  const [commentActive, setCommentActive] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const commentDisplayPlacementRef = useRef<string | undefined>(undefined);
  const pendingHighlightRef = useRef<HTMLDivElement | null>(null);
  const pendingHighlightRangeRef = useRef<Range | null>(null);
  const {collabOwner, collabId, planningId} = useCollabContext();

  const isDarkMode = useAtomValue(derivedThemeAtom) === 'dark';
  const employee = useAtomValue(employeeAtom);
  const isCommentDraftDirty = useAtomValue(commentDraftDirtyAtom);

  // Formatting state variables
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isHighlight, setIsHighlight] = useState(false);
  const [showHighlightColorToolbar, setShowHighlightColorToolbar] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isRedaction, setIsRedaction] = useState(false);
  const [isCreatingRedactionV2, setIsCreatingRedactionV2] = useState(false);
  const [canUseRedactionButton, setCanUseRedactionButton] = useState(false);
  const [isPlaceholder, setIsPlaceholder] = useState(false);
  const [isExcludeWordcount, setIsExcludeWordcount] = useState(false);
  const [blockType, setBlockType] = useState('paragraph');
  const [isInTableCell, setIsInTableCell] = useState(false);
  const [tableCellNode, setTableCellNode] = useState<TableCellNode | null>(null);
  const [isRowHeader, setIsRowHeader] = useState(false);
  const [isColumnHeader, setIsColumnHeader] = useState(false);
  const [tableRows, setTableRows] = useState(0);
  const [tableColumns, setTableColumns] = useState(0);
  const [currentRowIndex, setCurrentRowIndex] = useState<number | null>(null);
  const [currentColumnIndex, setCurrentColumnIndex] = useState<number | null>(null);
  const [tableSelectionInfo, setTableSelectionInfo] = useState<TableSelectionInfo | null>(null);
  // Track if the current selection can be safely wrapped (for comments/redactions)
  const [isSelectionWrappable, setIsSelectionWrappable] = useState(true);
  const [isSelectionBlockTransformable, setIsSelectionBlockTransformable] = useState(true);
  const lastRedactionV2SnapshotRef = useRef<RedactionV2SelectionSnapshot | null>(null);
  const lastRedactionV2EligibilityDebugRef = useRef<Record<string, unknown> | null>(null);
  const isHiddenForRedactionV2CreateRef = useRef(false);
  const hasHighlightControl =
    controls.includes('highlighting') || controls.includes('enhancements');
  const hasEnhancementsControl = controls.includes('enhancements');
  const canShowRedactionControl =
    hasEnhancementsControl && canUseRedactionButton && !isCreatingRedactionV2;

  const computeCommentDisplayMaxHeight = (
    rect: DOMRect | undefined | null,
    placementOverride?: string
  ) => getCommentDisplayMaxHeightForAnchor(rect, placementOverride ?? placement);

  useEffect(() => {
    return editor.registerCommand(
      TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND,
      ({open}) => {
        setIsVisible(open);
        return false;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  // Use atoms directly for AI suggestion and comment state (detection now handled in TextSelectionPlugin)
  const [activeAISuggestion, setActiveAISuggestion] = useAtom(activeAISuggestionAtom);
  const effectiveActiveAISuggestion = disableAISuggestions ? null : activeAISuggestion;
  const activeComment = useAtomValue(activeCommentAtom);
  const {openComment, closeComment} = useCommentActions();

  // Reset unattached comment ownership whenever the active comment changes threads
  useEffect(() => {
    if (activeComment?.threadId !== unattachedCommentOwnerThreadId) {
      unattachedCommentOwnerEditorKey = null;
      unattachedCommentOwnerThreadId = activeComment?.threadId ?? null;
    }
  }, [activeComment?.threadId]);

  // AI suggestion handlers
  const handleAISuggestionAccept = useCallback(
    (suggestion: AISuggestion) => {
      editor.update(
        () => {
          try {
            $setSelection(null);

            const suggestionMarkNodes = AISuggestionMarkNode.getById(suggestion.id);
            if (suggestionMarkNodes.length === 0) {
              console.warn('⚠️ Suggestion not found in document');
              return;
            }

            const allOriginalChildren: LexicalNode[] = [];
            suggestionMarkNodes.forEach((node) => {
              allOriginalChildren.push(...node.getChildren());
            });

            const newNodes = recreateFormattedNodes(allOriginalChildren, suggestion.suggestedText);

            if (suggestionMarkNodes.length === 1) {
              const suggestionMarkNode = suggestionMarkNodes[0];
              if (newNodes.length === 1) {
                suggestionMarkNode.replace(newNodes[0]);
              } else if (newNodes.length > 1) {
                newNodes.forEach((node, index) => {
                  if (index === 0) {
                    suggestionMarkNode.insertBefore(node);
                  } else {
                    newNodes[index - 1].insertAfter(node);
                  }
                });
                suggestionMarkNode.remove();
              } else {
                const fallbackTextNode = $createTextNode(suggestion.suggestedText);
                suggestionMarkNode.replace(fallbackTextNode);
              }
            } else {
              const firstNode = suggestionMarkNodes[0];
              if (newNodes.length === 1) {
                firstNode.replace(newNodes[0]);
              } else if (newNodes.length > 1) {
                newNodes.forEach((node, index) => {
                  if (index === 0) {
                    firstNode.insertBefore(node);
                  } else {
                    newNodes[index - 1].insertAfter(node);
                  }
                });
                firstNode.remove();
              } else {
                const fallbackTextNode = $createTextNode(suggestion.suggestedText);
                firstNode.replace(fallbackTextNode);
              }

              for (let i = 1; i < suggestionMarkNodes.length; i++) {
                suggestionMarkNodes[i].remove();
              }
            }
          } catch (error) {
            console.error('❌ Error in handleAISuggestionAccept:', error);
          }
        },
        {tag: AI_SUGGESTION_ACCEPTANCE_TAG}
      );

      setActiveAISuggestion(null);
    },
    [editor, setActiveAISuggestion]
  );

  const handleAISuggestionReject = useCallback(
    (suggestion: AISuggestion) => {
      editor.update(
        () => {
          try {
            $setSelection(null);

            const suggestionMarkNodes = AISuggestionMarkNode.getById(suggestion.id);
            if (suggestionMarkNodes.length === 0) {
              console.warn('⚠️ Suggestion not found for rejection');
              return;
            }

            suggestionMarkNodes.forEach((node) => {
              node.setRejected(true);
              node.markDirty();
            });
          } catch (error) {
            console.error('❌ Error in handleAISuggestionReject:', error);
          }
        },
        {tag: AI_SUGGESTION_ACCEPTANCE_TAG}
      );

      setActiveAISuggestion(null);
    },
    [editor, setActiveAISuggestion]
  );

  const handleAISuggestionClose = useCallback(
    (suggestionId?: string) => {
      // Only clear if no suggestionId provided, or if it matches the current one
      // This prevents closing when switching to a different AI suggestion
      if (!suggestionId || activeAISuggestion?.suggestion.id === suggestionId) {
        setActiveAISuggestion(null);
      }
    },
    [setActiveAISuggestion, activeAISuggestion]
  );

  const {threads} = useThreads();
  const redactionV2CommentThreadIds = useMemo(
    () => getRedactionV2CommentThreadIds(threads),
    [threads]
  );
  const redactionV2SelectionOptions = useMemo(
    () => ({commentThreadIds: redactionV2CommentThreadIds}),
    [redactionV2CommentThreadIds]
  );

  // Analyze table selection to determine if it's a full row/column selection
  const analyzeTableSelection = useCallback(
    (selection: TableSelection): TableSelectionInfo | null => {
      try {
        // Get the shape of the selection (rows x columns)
        const shape = selection.getShape();
        if (!shape) return null;

        const {fromX, fromY, toX, toY} = shape;

        // Get all selected cells
        const nodes = selection.getNodes();
        const tableCells = nodes.filter($isTableCellNode);

        if (tableCells.length === 0) return null;

        // Get table dimensions from the first cell
        const firstCell = tableCells[0];
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(firstCell);
        const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
        const totalRows = gridMap.length;
        const totalColumns = gridMap[0]?.length || 0;

        // Determine selected rows and columns
        const selectedRows: number[] = [];
        const selectedColumns: number[] = [];

        // Check if this is a full row selection
        const isFullRowSelection = toX - fromX + 1 === totalColumns;
        if (isFullRowSelection) {
          for (let row = fromY; row <= toY; row++) {
            selectedRows.push(row);
          }
        }

        // Check if this is a full column selection
        const isFullColumnSelection = toY - fromY + 1 === totalRows;
        if (isFullColumnSelection) {
          for (let col = fromX; col <= toX; col++) {
            selectedColumns.push(col);
          }
        }

        // Check if adding rows is enabled (not disabled) from the table node
        const enableAddRows = $isEnhancedTableNode(tableNode) ? tableNode.canAddRows() : true;
        const enableAddColumns = $isEnhancedTableNode(tableNode) ? tableNode.canAddColumns() : true;

        return {
          isFullRowSelection,
          isFullColumnSelection,
          selectedRows,
          selectedColumns,
          tableRows: totalRows,
          tableColumns: totalColumns,
          enableAddRows,
          enableAddColumns,
        };
      } catch (error) {
        console.warn('Error analyzing table selection:', error);
        return null;
      }
    },
    []
  );

  // Analyze table selection when it changes
  useEffect(() => {
    if (tableSelection) {
      editor.update(() => {
        const info = analyzeTableSelection(tableSelection);
        setTableSelectionInfo(info);
      });
    } else {
      setTableSelectionInfo(null);
    }
  }, [tableSelection, editor, analyzeTableSelection]);

  // Get collab context to determine which comment system to use

  // Handle optimistic comment thread opening after creation
  const handleCommentCreated = useCallback(
    ({threadId, editorKey, commentText, planningCommentOriginId}: CommentCreatedData) => {
      openComment({
        threadId,
        editorKey,
        source: 'create',
        planningCommentOriginId,
        optimisticComment: {
          text: commentText,
          authorId: employee?.user_id || '',
          authorName: employee?.name,
          createdAt: new Date().toISOString(),
        },
      });
    },
    [openComment, employee]
  );

  // Clear the pending highlight overlay
  const clearPendingHighlight = useCallback(() => {
    if (pendingHighlightRef.current) {
      pendingHighlightRef.current.remove();
      pendingHighlightRef.current = null;
    }
    pendingHighlightRangeRef.current = null;
  }, []);

  const handleCommentClose = useCallback(() => {
    if (!closeComment()) return;

    setCommentActive(false);
    commentDisplayPlacementRef.current = undefined;
    setIsSelectionActive(false);
    clearPendingHighlight();

    // Clear editor selection so selected text toolbar hides as well
    editor.update(() => {
      $setSelection(null);
    });
  }, [editor, closeComment, setCommentActive, setIsSelectionActive, clearPendingHighlight]);

  const renderPendingHighlight = useCallback((range: Range) => {
    const rects = Array.from(range.getClientRects());

    if (rects.length === 0) return;

    // Filter out duplicate/overlapping rects
    const filteredRects = rects.filter((rect, index) => {
      if (rect.width === 0 || rect.height === 0) return false;
      for (let i = 0; i < index; i++) {
        const other = rects[i];
        if (
          Math.abs(rect.top - other.top) < 2 &&
          Math.abs(rect.left - other.left) < 2 &&
          Math.abs(rect.width - other.width) < 2
        ) {
          return false;
        }
      }
      return true;
    });

    // Create container for highlight elements
    if (!pendingHighlightRef.current) {
      pendingHighlightRef.current = document.createElement('div');
      pendingHighlightRef.current.style.pointerEvents = 'none';
      pendingHighlightRef.current.style.position = 'absolute';
      pendingHighlightRef.current.style.top = '0';
      pendingHighlightRef.current.style.left = '0';
      pendingHighlightRef.current.style.zIndex = '1';
      document.body.appendChild(pendingHighlightRef.current);
    }

    pendingHighlightRef.current.innerHTML = '';

    filteredRects.forEach((rect) => {
      const highlight = document.createElement('span');
      highlight.style.position = 'absolute';
      highlight.style.top = `${rect.top + window.scrollY}px`;
      highlight.style.left = `${rect.left + window.scrollX}px`;
      highlight.style.width = `${rect.width}px`;
      highlight.style.height = `${rect.height}px`;
      highlight.classList.add(commentStyles.commenting);
      pendingHighlightRef.current?.appendChild(highlight);
    });
  }, []);

  // Create pending highlight overlay from the current selection
  const createPendingHighlight = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0).cloneRange();
    pendingHighlightRangeRef.current = range;
    renderPendingHighlight(range);
  }, [renderPendingHighlight]);

  // Handle comment button click
  const handleCommentButtonClick = useCallback(() => {
    if (!commentActive) {
      // Create the pending highlight BEFORE setting state
      createPendingHighlight();
      commentDisplayPlacementRef.current = placement;
    } else {
      // Clear the highlight when deactivating
      clearPendingHighlight();
      commentDisplayPlacementRef.current = undefined;
    }
    setCommentActive(!commentActive);
  }, [commentActive, setCommentActive, createPendingHighlight, clearPendingHighlight, placement]);

  // Clear pending highlight when commentActive becomes false (e.g., clicking outside)
  useEffect(() => {
    if (!commentActive) {
      commentDisplayPlacementRef.current = undefined;
      clearPendingHighlight();
    }
  }, [commentActive, clearPendingHighlight]);

  useEffect(() => {
    if (!commentActive || !pendingHighlightRangeRef.current) {
      return;
    }

    let frameId: number | null = null;
    const updatePendingHighlightPosition = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        frameId = null;
        const range = pendingHighlightRangeRef.current;
        if (!range) {
          return;
        }
        renderPendingHighlight(range);
      });
    };

    window.addEventListener('scroll', updatePendingHighlightPosition, true);
    window.addEventListener('resize', updatePendingHighlightPosition);

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      window.removeEventListener('scroll', updatePendingHighlightPosition, true);
      window.removeEventListener('resize', updatePendingHighlightPosition);
    };
  }, [commentActive, renderPendingHighlight]);

  // Cleanup pending highlight on unmount
  useEffect(() => {
    return () => {
      clearPendingHighlight();
    };
  }, [clearPendingHighlight]);

  // Update toolbar state based on current selection
  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElement();
      if (!element) {
        lastRedactionV2SnapshotRef.current = null;
        lastRedactionV2EligibilityDebugRef.current = {
          canUseRedactionButton: false,
          reason: 'no-top-level-element',
          anchorNode: describeRedactionV2Node(anchorNode),
        };
        debugRedactionV2Selection('toolbar-eligibility', {
          canUseRedactionButton: false,
          reason: 'no-top-level-element',
          anchorNode: describeRedactionV2Node(anchorNode),
        });
        return;
      }

      // Update block type (for list and heading detection)
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode);
        const listType = parentList ? parentList.getListType() : element.getListType();
        // Map list types to block types for consistency with RichTextToolbarPlugin
        if (listType === 'number') {
          setBlockType('ol');
        } else if (listType === 'bullet') {
          setBlockType('ul');
        } else if (listType === 'check') {
          setBlockType('check');
        } else {
          setBlockType('paragraph');
        }
      } else {
        // Check for headings or default to paragraph
        const type = $isEnhancedHeadingNode(element) ? element.getTag() : element.getType();
        setBlockType(type);
      }

      // Update text format states
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));

      // Check for highlight - either via format flag or inline background-color style
      const highlightColor = $getSelectionStyleValueForProperty(selection, 'background-color', '');
      const hasHighlightFormat = selection.hasFormat('highlight');
      setIsHighlight(hasHighlightFormat || !!highlightColor);

      // Update link state
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      // Update redaction state
      const redactionNode = $isRedactionNode(parent)
        ? parent
        : $isRedactionNode(node)
          ? node
          : null;
      const redactionV2Node = $isRedactionV2Node(parent)
        ? parent
        : $isRedactionV2Node(node)
          ? node
          : null;
      const isRedactionSelection = Boolean(redactionNode || redactionV2Node);
      setIsRedaction(isRedactionSelection);
      const shouldRestoreToolbarAfterRedactionV2Create =
        shouldRestoreSelectedTextToolbarAfterRedactionV2Create({
          isCollapsedSelection: selection.isCollapsed(),
          isRedactionSelection,
          wasHiddenForRedactionV2Create: isHiddenForRedactionV2CreateRef.current,
        });

      const currentRedactionV2SelectionResult = getRedactionV2SelectionSnapshotResult(
        selection,
        redactionV2SelectionOptions
      );
      const activeDomSelection = getActiveDomSelectionInEditor(editor.getRootElement());
      const activeDomSelectionText = activeDomSelection?.toString().trim() ?? '';
      const domRedactionV2SelectionResult =
        !currentRedactionV2SelectionResult.snapshot && activeDomSelection
          ? getRedactionV2SelectionSnapshotResultFromDomSelection(
              activeDomSelection,
              editor,
              redactionV2SelectionOptions
            )
          : null;
      const normalizedRedactionV2SelectionResult =
        currentRedactionV2SelectionResult.snapshot || !domRedactionV2SelectionResult
          ? currentRedactionV2SelectionResult
          : domRedactionV2SelectionResult;
      const isNormalizedRedactionV2SelectionCollapsed =
        normalizedRedactionV2SelectionResult.reason === 'selection-collapsed' ||
        normalizedRedactionV2SelectionResult.reason === 'dom-resolved-selection-collapsed';
      const cachedRedactionV2Snapshot = lastRedactionV2SnapshotRef.current;
      const redactionV2SelectionResult =
        normalizedRedactionV2SelectionResult.snapshot ||
        !isNormalizedRedactionV2SelectionCollapsed ||
        !activeDomSelectionText ||
        !cachedRedactionV2Snapshot ||
        cachedRedactionV2Snapshot.text.trim() !== activeDomSelectionText
          ? normalizedRedactionV2SelectionResult
          : {
              snapshot: cachedRedactionV2Snapshot,
              reason: 'using-cached-single-text-node-selection',
              details: {
                text: cachedRedactionV2Snapshot.text,
                domSelectionText: activeDomSelectionText,
              },
            };
      const canUseRedactionV2 =
        Boolean(collabId && hasEnhancementsControl) && redactionV2SelectionResult.snapshot !== null;
      if (redactionV2SelectionResult.snapshot) {
        lastRedactionV2SnapshotRef.current = redactionV2SelectionResult.snapshot;
      } else if (
        redactionV2SelectionResult.reason !== 'selection-collapsed' &&
        redactionV2SelectionResult.reason !== 'dom-resolved-selection-collapsed'
      ) {
        lastRedactionV2SnapshotRef.current = null;
      }

      // Update placeholder state
      const nextIsPlaceholder = $isPlaceholderNode(parent) || $isPlaceholderNode(node);
      setIsPlaceholder(nextIsPlaceholder);

      // Update exclude wordcount state
      const nextIsExcludeWordcount =
        $isExcludeWordcountNode(parent) || $isExcludeWordcountNode(node);
      setIsExcludeWordcount(nextIsExcludeWordcount);

      // Update table cell state - check if we're inside a table cell
      const currentTableCellNode = $findMatchingParent(anchorNode, $isTableCellNode);
      setIsInTableCell(currentTableCellNode !== null);

      if (currentTableCellNode) {
        setTableCellNode(currentTableCellNode);

        // Get table dimensions using gridMap
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(currentTableCellNode);
        const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
        const totalRows = gridMap.length;
        const totalColumns = gridMap[0]?.length || 0;

        setTableRows(totalRows);
        setTableColumns(totalColumns);

        // Find current cell position in the grid
        const currentCellKey = currentTableCellNode.getKey();
        let foundRowIndex: number | null = null;
        let foundColumnIndex: number | null = null;

        for (let rowIdx = 0; rowIdx < gridMap.length; rowIdx++) {
          for (let colIdx = 0; colIdx < gridMap[rowIdx].length; colIdx++) {
            if (gridMap[rowIdx][colIdx]?.cell?.getKey() === currentCellKey) {
              foundRowIndex = rowIdx;
              foundColumnIndex = colIdx;
              break;
            }
          }
          if (foundRowIndex !== null) break;
        }

        setCurrentRowIndex(foundRowIndex);
        setCurrentColumnIndex(foundColumnIndex);

        // Check if top row (row 0) has row headers
        const topRowCell = gridMap[0]?.[0]?.cell;
        const rowHeaderState =
          topRowCell &&
          (topRowCell.getHeaderStyles() & TableCellHeaderStates.ROW) === TableCellHeaderStates.ROW;
        setIsRowHeader(rowHeaderState);

        // Check if left column (column 0) has column headers
        const leftColumnCell = gridMap[0]?.[0]?.cell;
        const columnHeaderState =
          leftColumnCell &&
          (leftColumnCell.getHeaderStyles() & TableCellHeaderStates.COLUMN) ===
            TableCellHeaderStates.COLUMN;
        setIsColumnHeader(columnHeaderState);
      } else {
        setTableCellNode(null);
        setIsRowHeader(false);
        setIsColumnHeader(false);
        setTableRows(0);
        setTableColumns(0);
        setCurrentRowIndex(null);
        setCurrentColumnIndex(null);
      }

      const selectionAnalysis = getSelectionAnalysis(selection);
      const selectionCapabilities = deriveSelectionCapabilities(selectionAnalysis);
      const globalSelection = window.getSelection();
      const activeRange =
        globalSelection && globalSelection.rangeCount > 0 ? globalSelection.getRangeAt(0) : null;
      const allowMultiBlockCommentWrapping =
        !selectionCapabilities.isWrappable &&
        shouldAllowMultiBlockCommentWrapping(editor.getRootElement(), activeRange);

      setCanUseRedactionButton(canUseRedactionV2);
      setIsSelectionWrappable(selectionCapabilities.isWrappable || allowMultiBlockCommentWrapping);
      setIsSelectionBlockTransformable(selectionCapabilities.isBlockTransformable);
      if (shouldRestoreToolbarAfterRedactionV2Create) {
        isHiddenForRedactionV2CreateRef.current = false;
        setIsVisible(true);
      }

      const eligibilityDebug = {
        canUseRedactionButton: canUseRedactionV2,
        collabIdPresent: Boolean(collabId),
        currentSnapshotDetails: currentRedactionV2SelectionResult.details,
        currentSnapshotReason: currentRedactionV2SelectionResult.reason,
        domSelectionText: activeDomSelectionText,
        domSnapshotDetails: domRedactionV2SelectionResult?.details ?? null,
        domSnapshotReason: domRedactionV2SelectionResult?.reason ?? null,
        hasCachedSnapshot: Boolean(cachedRedactionV2Snapshot),
        cachedSnapshotText: cachedRedactionV2Snapshot?.text ?? null,
        hasEnhancementsControl,
        hasHighlightControl,
        isSelectionWrappable: selectionCapabilities.isWrappable || allowMultiBlockCommentWrapping,
        isPlaceholder: nextIsPlaceholder,
        isExcludeWordcount: nextIsExcludeWordcount,
        snapshotReason: redactionV2SelectionResult.reason,
        snapshotDetails: redactionV2SelectionResult.details,
        selectionAnalysis,
        selectionCapabilities,
        selectedNode: describeRedactionV2Node(node),
        parentNode: parent ? describeRedactionV2Node(parent) : null,
      };
      lastRedactionV2EligibilityDebugRef.current = eligibilityDebug;
      debugRedactionV2Selection('toolbar-eligibility', eligibilityDebug);
    } else {
      lastRedactionV2SnapshotRef.current = null;
      lastRedactionV2EligibilityDebugRef.current = {
        canUseRedactionButton: false,
        reason: 'no-range-selection',
        selectionType: selection?.constructor?.name,
      };
      setCanUseRedactionButton(false);
      setIsRedaction(false);
      debugRedactionV2Selection('toolbar-eligibility', {
        canUseRedactionButton: false,
        reason: 'no-range-selection',
        selectionType: selection?.constructor?.name,
      });
    }
  }, [collabId, editor, hasEnhancementsControl, hasHighlightControl, redactionV2SelectionOptions]);

  const handleLinkToggle = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
        url: '',
        target: '_blank',
        rel: 'noopener noreferrer',
      });
      setIsVisible(false); // hide toolbar when link editor is opened
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const formatHeading = useCallback(
    (headingSize: 'h1' | 'h2' | 'h3') => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          if (blockType === headingSize) {
            // If already this heading, convert back to paragraph
            $setBlocksType(selection, () => $createParagraphNode());
          } else {
            // Convert to heading with a unique ID
            const headingId = `heading-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
            $setBlocksType(selection, () =>
              $createEnhancedHeadingNode(headingSize, false, {}, headingId)
            );
          }
        }
      });
    },
    [editor, blockType]
  );

  const formatBulletList = useCallback(() => {
    if (blockType !== 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      // Use $setBlocksType to only convert selected items to paragraphs,
      // not the entire list
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      });
    }
  }, [editor, blockType]);

  const formatNumberedList = useCallback(() => {
    if (blockType !== 'ol') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      // Use $setBlocksType to only convert selected items to paragraphs,
      // not the entire list
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      });
    }
  }, [editor, blockType]);

  const formatCheckList = useCallback(() => {
    if (blockType !== 'check') {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    } else {
      // Use $setBlocksType to only convert selected items to paragraphs,
      // not the entire list
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      });
    }
  }, [editor, blockType]);

  // Apply highlight color to selected text using inline styles
  const applyTextHighlightColor = useCallback(
    (color: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {'background-color': color});
        }
      });
      setShowHighlightColorToolbar(false);
    },
    [editor]
  );

  // Clear highlight from selected text
  const clearTextHighlight = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, {'background-color': null});
      }
    });
    setShowHighlightColorToolbar(false);
  }, [editor]);

  // Handle highlight button click - show color picker
  const handleHighlightClick = useCallback(() => {
    setShowHighlightColorToolbar((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isSelectionActive) {
      if (commentActive && isCommentDraftDirty && !confirmDiscardCommentDraft()) {
        setIsSelectionActive(true);
        return;
      }

      setCommentActive(false);
      setShowHighlightColorToolbar(false);
      lastRedactionV2SnapshotRef.current = null;
    }
  }, [commentActive, isCommentDraftDirty, isSelectionActive, setIsSelectionActive]);

  // Register editor listeners for formatting state updates
  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({editorState}) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          // Close highlight color toolbar when selection changes
          setShowHighlightColorToolbar(false);
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateToolbar();
    });
  }, [editor, updateToolbar]);

  const createRedactionV2FromSelection = useCallback(async () => {
    if (!collabId || !hasEnhancementsControl || isCreatingRedactionV2) {
      return;
    }

    const snapshot = editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const lexicalResult = getRedactionV2SelectionSnapshotResult(
          selection,
          redactionV2SelectionOptions
        );
        if (lexicalResult.snapshot) {
          return lexicalResult.snapshot;
        }

        const activeDomSelection = getActiveDomSelectionInEditor(editor.getRootElement());
        const activeDomSelectionText = activeDomSelection?.toString().trim() ?? '';
        const domResult = activeDomSelection
          ? getRedactionV2SelectionSnapshotResultFromDomSelection(
              activeDomSelection,
              editor,
              redactionV2SelectionOptions
            )
          : null;
        if (domResult?.snapshot) {
          return domResult.snapshot;
        }

        const normalizedResult = domResult ?? lexicalResult;
        const canUseCachedSnapshot =
          (normalizedResult.reason === 'selection-collapsed' ||
            normalizedResult.reason === 'dom-resolved-selection-collapsed') &&
          activeDomSelectionText.length > 0 &&
          lastRedactionV2SnapshotRef.current?.text.trim() === activeDomSelectionText;

        return canUseCachedSnapshot ? lastRedactionV2SnapshotRef.current : null;
      }
      return null;
    });

    if (!snapshot) {
      return;
    }

    isHiddenForRedactionV2CreateRef.current = true;
    setIsVisible(false);
    setIsCreatingRedactionV2(true);
    try {
      const draft = await getService(
        DocumentService
      ).createRedactionDraftApiV1DocumentDocumentIdRedactionsDraftPost(collabId, {
        redaction_id: snapshot.redactionId,
        text: snapshot.text,
        content: snapshot.content,
        allowed_employee_ids: getDefaultRedactionV2AllowedEmployeeIds(employee, collabOwner),
      });

      let insertedNodeKey: string | null = null;
      editor.update(() => {
        restoreRedactionV2Selection(snapshot);
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return;
        }
        const currentSnapshot = getRedactionV2SelectionSnapshot(
          selection,
          redactionV2SelectionOptions
        );
        if (
          !currentSnapshot ||
          currentSnapshot.text !== snapshot.text ||
          JSON.stringify(currentSnapshot.content) !== JSON.stringify(snapshot.content)
        ) {
          return;
        }

        const redactionNode = $createRedactionV2Node({
          redactionId: draft.id,
          maskSize: draft.mask_size,
        });
        insertedNodeKey = redactionNode.getKey();
        selection.insertNodes([redactionNode]);
      });

      if (!insertedNodeKey) {
        isHiddenForRedactionV2CreateRef.current = false;
        setIsVisible(true);
        await getService(
          DocumentService
        ).beginRedactionRemovalApiV1DocumentDocumentIdRedactionsRedactionIdBeginRemovalPost(
          collabId,
          draft.id
        );
        await getService(
          DocumentService
        ).finalizeRedactionRemovalApiV1DocumentDocumentIdRedactionsRedactionIdFinalizeRemovalPost(
          collabId,
          draft.id
        );
        return;
      }

      editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
      editor.dispatchCommand(TOGGLE_REDACTION_V2_EDITOR_COMMAND, {
        nodeKey: insertedNodeKey,
        redactionId: draft.id,
      });

      await activateRedactionV2WithRetry({
        documentId: collabId,
        redactionId: draft.id,
        maskSize: draft.mask_size,
      });
      editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
    } catch (error) {
      isHiddenForRedactionV2CreateRef.current = false;
      setIsVisible(true);
      console.error('Failed to create redaction v2', error);
    } finally {
      setIsCreatingRedactionV2(false);
    }
  }, [
    collabId,
    collabOwner,
    editor,
    employee,
    hasEnhancementsControl,
    isCreatingRedactionV2,
    redactionV2SelectionOptions,
  ]);

  const handleRedactionClick = useCallback(() => {
    if (hasEnhancementsControl) {
      void createRedactionV2FromSelection();
    }
  }, [createRedactionV2FromSelection, hasEnhancementsControl]);

  const handlePlaceholderClick = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection);
        const parent = node.getParent();

        // Check if we're in a placeholder - if so, remove it
        if ($isPlaceholderNode(parent)) {
          $unwrapMarkNode(parent as any);
        } else if ($isPlaceholderNode(node)) {
          $unwrapMarkNode(node as any);
        } else if (!selection.isCollapsed()) {
          // Only add placeholder if text is selected
          const placeholderId = `placeholder-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

          $wrapSelectionInPlaceholderNode(selection, placeholderId);
        }
      }
    });
  }, [editor]);

  const handleExcludeWordcountClick = useCallback(
    (isDeletable: boolean = true) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const node = getSelectedNode(selection);
          const parent = node.getParent();

          // Check if we're in an exclude wordcount - if so, remove it
          if ($isExcludeWordcountNode(parent)) {
            $unwrapMarkNode(parent as any);
          } else if ($isExcludeWordcountNode(node)) {
            $unwrapMarkNode(node as any);
          } else if (!selection.isCollapsed()) {
            // Only add exclude wordcount if text is selected
            const excludeId = `exclude-wordcount-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

            $wrapSelectionInExcludeWordcountNode(selection, excludeId, undefined, isDeletable);
          }
        }
      });
    },
    [editor]
  );

  const handleTypeaheadClick = () => {
    // const label = employee;
    editor.update(() => {
      const node = $createTypeaheadNode('employee', undefined);
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $insertNodes([node]);
      } else {
        // Fallback: append in a new paragraph
        const p = $createParagraphNode();
        p.append(node);
        $insertNodes([p]);
      }
    });
  };

  const toggleTableRowIsHeader = useCallback(() => {
    if (!tableCellNode) return;

    try {
      toggleTableRowHeaders(editor, tableCellNode, true);
    } catch (error) {
      console.error('Error toggling row headers:', error);
    }
  }, [editor, tableCellNode]);

  const toggleTableColumnIsHeader = useCallback(() => {
    if (!tableCellNode) return;

    try {
      toggleTableColumnHeaders(editor, tableCellNode, true);
    } catch (error) {
      console.error('Error toggling column headers:', error);
    }
  }, [editor, tableCellNode]);

  // Keyboard shortcuts for custom toolbar features
  // Using COMMAND_PRIORITY_HIGH to run before ShortcutsPlugin which has dead handlers
  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        const {code, metaKey, ctrlKey, altKey, shiftKey} = event;
        const isModKey = IS_APPLE ? metaKey : ctrlKey;

        // Cmd/Ctrl + Shift shortcuts
        if (isModKey && shiftKey && !altKey) {
          if (code === 'KeyK') {
            event.preventDefault();
            handleLinkToggle();
            return true;
          }
        }

        // Cmd/Ctrl + Option/Alt shortcuts
        if (isModKey && altKey && !shiftKey) {
          if (hasEnhancementsControl && canUseRedactionButton && code === 'KeyR') {
            event.preventDefault();
            void handleRedactionClick();
            return true;
          }
          if (code === 'Digit1' && isTemplateMode) {
            event.preventDefault();
            formatHeading('h1');
            return true;
          }
          if (code === 'Digit2' && isTemplateMode) {
            event.preventDefault();
            formatHeading('h2');
            return true;
          }
          if (code === 'KeyP') {
            event.preventDefault();
            handlePlaceholderClick();
            return true;
          }
          if (code === 'KeyW') {
            event.preventDefault();
            handleExcludeWordcountClick(true);
            return true;
          }
          if (code === 'KeyM') {
            event.preventDefault();
            handleCommentButtonClick();
            return true;
          }
          if (code === 'Digit7') {
            event.preventDefault();
            formatNumberedList();
            return true;
          }
          if (code === 'Digit8') {
            event.preventDefault();
            formatBulletList();
            return true;
          }
          if (code === 'Digit9') {
            event.preventDefault();
            formatCheckList();
            return true;
          }
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [
    editor,
    canUseRedactionButton,
    handleRedactionClick,
    handleLinkToggle,
    formatHeading,
    handlePlaceholderClick,
    handleExcludeWordcountClick,
    handleCommentButtonClick,
    formatNumberedList,
    formatBulletList,
    formatCheckList,
    hasEnhancementsControl,
    isTemplateMode,
  ]);

  // Show comment thread display if there's an active comment
  if (activeComment) {
    const editorKey = editor.getKey();
    const isOwningCommentEditor = !activeComment.editorKey || activeComment.editorKey === editorKey;

    // Non-owning editors never render the comment UI — this prevents the outer editor
    // (or sibling nested editors) from flickering the main toolbar / duplicating CommentDisplay
    // while the owning editor handles the thread.
    if (!isOwningCommentEditor) {
      return null;
    }

    const thread = threads?.find((th) => th.id === activeComment.threadId);
    const hasOptimistic = Boolean(activeComment.optimisticComment);

    // Nothing to render yet — wait for either a resolved thread or an optimistic placeholder.
    // Returning null keeps this owning toolbar hidden instead of flashing the formatting buttons.
    if (!thread && !hasOptimistic) {
      return null;
    }

    const editorElement = editor.getRootElement();
    const highlightElement = findHighlightSpanOwnedByEditor(editorElement, activeComment.threadId);
    if (
      planningId &&
      highlightElement &&
      !shouldRenderActiveCommentForHighlight({
        activeComment,
        editorElement,
        highlightElement,
      })
    ) {
      return null;
    }

    // If this editor is nested inside another Lexical editor, anchor the comment box directly
    // to the highlight rect using fixed positioning. This bypasses the shared Floating UI box
    // (used for the formatting toolbar) which can briefly render at (0, 0) before a reference
    // is wired up for nested instances, producing the top-left flicker.
    const isNestedEditor = Boolean(editorElement?.parentElement?.closest('[data-lexical-editor]'));

    if (highlightElement) {
      if (
        !shouldRenderActiveCommentForHighlight({
          activeComment,
          editorElement,
          highlightElement,
        })
      ) {
        return null;
      }

      const highlightRect = highlightElement.getBoundingClientRect();
      const maxHeight = computeCommentDisplayMaxHeight(highlightRect);

      if (planningId) {
        return (
          <CommentRailPortal threadId={activeComment.threadId}>
            <CommentDisplay
              thread={thread}
              onClose={handleCommentClose}
              maxHeightForInlineComment={maxHeight}
              commentBoxMaxHeight={COMMENTS_PANEL_COMMENT_BOX_MAX_HEIGHT}
              autoFocusInput={activeCommentInputAutoFocus}
              commentMentions={commentMentions}
            />
          </CommentRailPortal>
        );
      }

      if (isNestedEditor) {
        const {top, left, width} = getNestedCommentDisplayPosition(highlightRect, maxHeight);
        return (
          <Box position="fixed" style={{top, left, width, zIndex: 30}}>
            <CommentDisplay
              thread={thread}
              onClose={handleCommentClose}
              maxHeightForInlineComment={maxHeight}
              autoFocusInput={activeCommentInputAutoFocus}
              commentMentions={commentMentions}
            />
          </Box>
        );
      }

      // Top-level editor (non-redesign): keep the existing Floating UI anchoring, which
      // positions the enclosing Box against the selection / highlight virtual element.
      return (
        <CommentDisplay
          thread={thread}
          onClose={handleCommentClose}
          maxHeightForInlineComment={maxHeight}
          autoFocusInput={activeCommentInputAutoFocus}
          commentMentions={commentMentions}
        />
      );
    }

    // No highlight owned by this editor — check if it lives in another editor instance
    const highlightElementAnywhere = document.querySelector<HTMLElement>(
      getCommentHighlightSelector(activeComment.threadId)
    );
    if (highlightElementAnywhere) {
      // A different editor instance owns the highlight DOM — avoid duplicate/mis-positioned UI
      return null;
    }

    // Unattached thread — only let one toolbar take ownership of rendering it
    if (unattachedCommentOwnerEditorKey && unattachedCommentOwnerEditorKey !== editorKey) {
      return null;
    }
    if (!unattachedCommentOwnerEditorKey) {
      unattachedCommentOwnerEditorKey = editorKey;
      unattachedCommentOwnerThreadId = activeComment.threadId;
    }
    const fallbackMaxHeight = computeCommentDisplayMaxHeight(
      editorElement?.getBoundingClientRect()
    );
    if (planningId) {
      return (
        <CommentRailPortal threadId={activeComment.threadId}>
          <CommentDisplay
            thread={thread}
            onClose={handleCommentClose}
            maxHeightForInlineComment={fallbackMaxHeight}
            commentBoxMaxHeight={COMMENTS_PANEL_COMMENT_BOX_MAX_HEIGHT}
            autoFocusInput={activeCommentInputAutoFocus}
            commentMentions={commentMentions}
          />
        </CommentRailPortal>
      );
    }
    if (isNestedEditor) {
      const anchorRect = editorElement?.getBoundingClientRect();
      if (anchorRect) {
        const {top, left, width} = getNestedCommentDisplayPosition(anchorRect, fallbackMaxHeight);
        return (
          <Box position="fixed" style={{top, left, width, zIndex: 30}}>
            <CommentDisplay
              thread={thread}
              onClose={handleCommentClose}
              maxHeightForInlineComment={fallbackMaxHeight}
              autoFocusInput={activeCommentInputAutoFocus}
              commentMentions={commentMentions}
            />
          </Box>
        );
      }
    }
    return (
      <CommentDisplay
        thread={thread}
        onClose={handleCommentClose}
        maxHeightForInlineComment={fallbackMaxHeight}
        autoFocusInput={activeCommentInputAutoFocus}
        commentMentions={commentMentions}
      />
    );
  }

  const isEditorEditable = editor.isEditable();
  const redactionV2EligibilityDebug = lastRedactionV2EligibilityDebugRef.current;
  const redactionV2EligibilityReason =
    typeof redactionV2EligibilityDebug?.snapshotReason === 'string'
      ? redactionV2EligibilityDebug.snapshotReason
      : typeof redactionV2EligibilityDebug?.reason === 'string'
        ? redactionV2EligibilityDebug.reason
        : 'unknown';
  const redactionControlBlockers = compactDebugReasons([
    !hasEnhancementsControl && 'missing-enhancements-control',
    !canUseRedactionButton && `selection-not-eligible:${redactionV2EligibilityReason}`,
    isCreatingRedactionV2 && 'redaction-create-in-flight',
  ]);
  const enhancementGroupBlockers = compactDebugReasons([
    !isSelectionActive && 'selection-inactive',
    !hasHighlightControl && 'missing-highlight-or-enhancements-control',
    isPlaceholder && !canShowRedactionControl && 'placeholder-without-redaction-control',
    !isSelectionWrappable &&
      !(hasEnhancementsControl && canShowRedactionControl) &&
      'selection-not-wrappable-and-redaction-hidden',
  ]);
  const renderSelectionEnhancementGroup =
    isSelectionActive &&
    hasHighlightControl &&
    (!isPlaceholder || canShowRedactionControl) &&
    (isSelectionWrappable || (hasEnhancementsControl && canShowRedactionControl));
  const redactionButtonBlockers = compactDebugReasons([
    !isEditorEditable && 'editor-readonly',
    !renderSelectionEnhancementGroup &&
      `enhancement-group-hidden:${enhancementGroupBlockers.join('|') || 'unknown'}`,
    !hasEnhancementsControl && 'missing-enhancements-control',
    !canShowRedactionControl &&
      `redaction-control-hidden:${redactionControlBlockers.join('|') || 'unknown'}`,
  ]);
  const renderRedactionButton =
    isEditorEditable &&
    renderSelectionEnhancementGroup &&
    hasEnhancementsControl &&
    canShowRedactionControl;

  // Show AI suggestion toolbar if there's an active suggestion and delay has passed
  if (effectiveActiveAISuggestion) {
    if (isMouseDown) {
      debugRedactionV2Selection('render-hidden', {
        reason: 'active-ai-suggestion-mouse-down',
        redactionButtonBlockers,
        redactionV2Eligibility: redactionV2EligibilityDebug,
      });
      return null;
    }
    debugRedactionV2Selection('render-hidden', {
      reason: 'active-ai-suggestion-toolbar',
      redactionButtonBlockers,
      redactionV2Eligibility: redactionV2EligibilityDebug,
    });
    return (
      <AISuggestionToolbar
        suggestion={effectiveActiveAISuggestion.suggestion}
        headingId={effectiveActiveAISuggestion.headingId}
        onAccept={handleAISuggestionAccept}
        onReject={handleAISuggestionReject}
        onClose={handleAISuggestionClose}
      />
    );
  }

  if (commentActive) {
    const commentDisplayMaxHeight = computeCommentDisplayMaxHeight(
      floatingReferenceRect,
      commentDisplayPlacementRef.current
    );
    debugRedactionV2Selection('render-hidden', {
      reason: 'comment-toolbar-active',
      redactionButtonBlockers,
      redactionV2Eligibility: redactionV2EligibilityDebug,
    });
    return (
      <CommentToolbar
        commentActive={commentActive}
        setCommentActive={setCommentActive}
        onCommentClose={handleCommentClose}
        onCommentCreated={handleCommentCreated}
        onClearPendingHighlight={clearPendingHighlight}
        maxHeight={commentDisplayMaxHeight}
        commentMentions={commentMentions}
      />
    );
  }

  if (!isVisible) {
    debugRedactionV2Selection('render-hidden', {
      reason: 'toolbar-not-visible',
      canShowRedactionControl,
      canUseRedactionButton,
      isSelectionActive,
      redactionButtonBlockers,
      redactionControlBlockers,
      redactionV2Eligibility: redactionV2EligibilityDebug,
    });
    return null;
  }

  debugRedactionV2Selection('render-gates', {
    canShowRedactionControl,
    canUseRedactionButton,
    controls,
    hasEnhancementsControl,
    hasHighlightControl,
    isCreatingRedactionV2,
    isPlaceholder,
    isSelectionActive,
    isSelectionBlockTransformable,
    isSelectionWrappable,
    redactionButtonBlockers,
    redactionControlBlockers,
    redactionV2Eligibility: redactionV2EligibilityDebug,
    renderEnhancementGroup: renderSelectionEnhancementGroup,
    renderRedactionButton,
  });
  if (!renderRedactionButton && hasEnhancementsControl) {
    debugRedactionV2Selection('redaction-button-hidden', {
      canShowRedactionControl,
      canUseRedactionButton,
      redactionButtonBlockers,
      redactionControlBlockers,
      redactionV2Eligibility: redactionV2EligibilityDebug,
      renderEnhancementGroup: renderSelectionEnhancementGroup,
      renderRedactionButton,
    });
  }

  // Show table selection toolbar if we have full row/column selection
  if (
    tableSelectionInfo &&
    controls.includes('table') &&
    (tableSelectionInfo.isFullColumnSelection || tableSelectionInfo.isFullRowSelection)
  ) {
    return (
      <TableToolbar
        tableSelection={tableSelection || null}
        tableSelectionInfo={tableSelectionInfo}
      />
    );
  }

  // Hide toolbar for partial table selections (not full row/column)
  if (tableSelection && !tableSelectionInfo) {
    debugRedactionV2Selection('render-hidden', {
      reason: 'partial-table-selection',
      tableSelectionPresent: true,
      tableSelectionInfo,
      redactionButtonBlockers,
      redactionV2Eligibility: redactionV2EligibilityDebug,
    });
    return null;
  }

  // Hide toolbar for table selections that don't meet our criteria
  if (
    tableSelectionInfo &&
    !(tableSelectionInfo.isFullColumnSelection || tableSelectionInfo.isFullRowSelection)
  ) {
    debugRedactionV2Selection('render-hidden', {
      reason: 'unsupported-table-selection',
      tableSelectionInfo,
      redactionButtonBlockers,
      redactionV2Eligibility: redactionV2EligibilityDebug,
    });
    return null;
  }

  if (!isSelectionActive && !isEditorEditable) {
    debugRedactionV2Selection('render-hidden', {
      reason: 'inactive-readonly-editor',
      isSelectionActive,
      isEditable: isEditorEditable,
      redactionButtonBlockers,
      redactionV2Eligibility: redactionV2EligibilityDebug,
    });
    return null;
  }

  // A multi-block selection in a comment-only surface cannot be wrapped in a
  // comment highlight. Without an available action, rendering the toolbar shell
  // leaves a small floating dot behind the selection.
  if (
    shouldHideUnsupportedCommentOnlyToolbar({
      controls,
      isSelectionActive,
      isSelectionWrappable,
    })
  ) {
    return null;
  }

  return (
    <ThemeProvider mode={!isDarkMode ? 'dark' : 'bright'}>
      <ThemeContainer>
        {/* <Tooltip.Group> */}
        <Box
          data-selection-toolbar
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          style={{gap: 8}}
        >
          <Box display="flex" flexDirection="row" style={{gap: 12}}>
            <Box
              ref={toolbarRef}
              boxShadow="Floating"
              backgroundColor="Background Base"
              borderRadius="Large"
              paddingY="4"
              paddingX={controls.filter((control) => control !== 'comments').length > 1 ? '8' : '4'}
              display="flex"
              flexDirection="row"
              style={{gap: 8}}
            >
              {isEditorEditable && (
                <>
                  {!isPlaceholder && isSelectionActive && controls.includes('styling') && (
                    <>
                      <ToolbarButton
                        title={t('toggle_bold')}
                        onClick={() => {
                          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
                        }}
                        icon={BoldLine}
                        active={isBold}
                        keycaps={['Command', 'B']}
                      />
                      <ToolbarButton
                        title={t('toggle_italic')}
                        onClick={() => {
                          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
                        }}
                        icon={ItalicLine}
                        active={isItalic}
                        keycaps={['Command', 'I']}
                      />
                      <ToolbarButton
                        title={t('toggle_underline')}
                        onClick={() => {
                          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
                        }}
                        icon={UnderlineLine}
                        active={isUnderline}
                        keycaps={['Command', 'U']}
                      />
                      <ToolbarButton
                        title={t('toggle_strikethrough')}
                        onClick={() => {
                          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
                        }}
                        icon={StrikethroughLine}
                        active={isStrikethrough}
                        keycaps={['Command', 'Shift', 'X']}
                      />
                      {(isSelectionWrappable ||
                        (hasEnhancementsControl && canUseRedactionButton)) &&
                        controls.filter((control) => !['styling', 'ai'].includes(control)).length >
                          1 && (
                          <Box
                            borderLeft="Solid"
                            marginTop="6"
                            marginLeft="2"
                            paddingLeft="2"
                            style={{height: 20}}
                          />
                        )}
                    </>
                  )}
                  {renderSelectionEnhancementGroup && (
                    <>
                      {!isPlaceholder && isSelectionWrappable && (
                        <ToolbarButton
                          title={t('toggle_highlight')}
                          onClick={handleHighlightClick}
                          icon={HighlightLine}
                          active={isHighlight || showHighlightColorToolbar}
                          keycaps={['Command', 'Option', 'E']}
                        />
                      )}

                      {hasEnhancementsControl && (
                        <>
                          {!isPlaceholder && isSelectionWrappable && (
                            <ToolbarButton
                              title={t('toggle_link')}
                              onClick={handleLinkToggle}
                              icon={LinkLine}
                              active={isLink}
                              keycaps={['Command', 'Shift', 'K']}
                            />
                          )}
                          {renderRedactionButton && (
                            <ToolbarButton
                              title={t('toggle_redaction')}
                              onClick={() => {
                                void handleRedactionClick();
                              }}
                              icon={RedactLine}
                              active={isRedaction}
                              keycaps={['Command', 'Option', 'R']}
                            />
                          )}
                        </>
                      )}

                      {controls.filter(
                        (control) => !['enhancements', 'highlighting', 'ai'].includes(control)
                      ).length > 1 && (
                        <Box
                          borderLeft="Solid"
                          marginTop="6"
                          marginLeft="2"
                          paddingLeft="2"
                          style={{height: 20}}
                        />
                      )}
                    </>
                  )}
                  {!isInTableCell && isTemplateMode && controls.includes('headers') && (
                    <>
                      <ToolbarButton
                        title={t('toggle_h1')}
                        onClick={() => formatHeading('h1')}
                        icon={H1Line}
                        active={blockType === 'h1'}
                        keycaps={['Command', 'Option', '1']}
                      />
                      <ToolbarButton
                        title={t('toggle_h2')}
                        onClick={() => formatHeading('h2')}
                        icon={H2Line}
                        active={blockType === 'h2'}
                        keycaps={['Command', 'Option', '2']}
                      />
                      {controls.filter((control) => !['headers', 'ai'].includes(control)).length >
                        1 && (
                        <Box
                          borderLeft="Solid"
                          marginTop="6"
                          marginLeft="2"
                          paddingLeft="2"
                          style={{height: 20}}
                        />
                      )}
                    </>
                  )}
                  {isTemplateMode &&
                    isSelectionActive &&
                    isSelectionWrappable &&
                    controls.includes('templates') && (
                      <>
                        <ToolbarButton
                          title={t('toggle_placeholder')}
                          onClick={handlePlaceholderClick}
                          icon={PlaceholderTextLine}
                          active={isPlaceholder}
                          keycaps={['Command', 'Option', 'P']}
                        />
                        <ToolbarButton
                          title="Exclude from wordcount"
                          onClick={() => handleExcludeWordcountClick(true)}
                          icon={HashtagLine}
                          active={isExcludeWordcount}
                          keycaps={['Command', 'Option', 'W']}
                        />
                        {/* <ToolbarButton
                        title="Exclude from wordcount (protected)"
                        onClick={() => handleExcludeWordcountClick(false)}
                        icon={ShieldLine}
                        active={isExcludeWordcount}
                        keycaps={['Command', 'Shift', 'Option', 'W']}
                      /> */}
                      </>
                    )}
                  {isTemplateMode && controls.includes('templates') && (
                    <>
                      <ToolbarButton
                        title={t('add_typeahead')}
                        icon={DropdownLine}
                        onClick={handleTypeaheadClick}
                      />
                      <DynamicNodeInsertToolbarButton />
                    </>
                  )}
                  {(!isSelectionActive || isSelectionBlockTransformable) &&
                    controls.includes('insertions') && (
                      <>
                        {(!isTemplateMode || isSelectionActive) && (
                          <ToolbarButton
                            title={t('toggle_bulleted_list')}
                            onClick={formatBulletList}
                            icon={UnorderedListLine}
                            active={blockType === 'ul'}
                            keycaps={['Command', 'Option', '8']}
                          />
                        )}
                        {(!isTemplateMode || isSelectionActive) && (
                          <ToolbarButton
                            title={t('toggle_numbered_list')}
                            onClick={formatNumberedList}
                            icon={OrderedListLine}
                            active={blockType === 'ol'}
                            keycaps={['Command', 'Option', '7']}
                          />
                        )}
                        {(!isTemplateMode || isSelectionActive) && (
                          <ToolbarButton
                            title={t('toggle_to_do_list')}
                            onClick={formatCheckList}
                            icon={ToDoLine}
                            active={blockType === 'check'}
                            keycaps={['Command', 'Option', '9']}
                          />
                        )}
                        {!isInTableCell && isTemplateMode && !isSelectionActive && (
                          <TableToolbarButton />
                        )}
                        {!isSelectionActive && <ImageToolbarButton />}
                        {isSelectionActive &&
                          controls.filter((control) => !['insertions', 'ai'].includes(control))
                            .length > 1 && (
                            <Box
                              borderLeft="Solid"
                              marginTop="6"
                              marginLeft="2"
                              paddingLeft="2"
                              style={{height: 20}}
                            />
                          )}
                      </>
                    )}
                </>
              )}
              {isSelectionActive && controls.includes('comments') && isSelectionWrappable && (
                <CommentToolbarButton
                  commentActive={commentActive}
                  setCommentActive={setCommentActive}
                  onClick={handleCommentButtonClick}
                />
              )}
              {!isSelectionActive &&
                isSelectionWrappable &&
                isTemplateMode &&
                isInTableCell &&
                controls.includes('table') &&
                !(tableRows === 1 && tableColumns === 1) && (
                  <>
                    {tableRows > 1 && currentRowIndex === 0 && (
                      <ToolbarButton
                        title={isRowHeader ? t('remove_row_header') : t('add_row_header')}
                        onClick={toggleTableRowIsHeader}
                        icon={TableHeaderRowLine}
                        active={isRowHeader}
                      />
                    )}
                    {tableColumns > 1 && currentColumnIndex === 0 && (
                      <ToolbarButton
                        title={isColumnHeader ? t('remove_column_header') : t('add_column_header')}
                        onClick={toggleTableColumnIsHeader}
                        icon={TableHeaderColumnLine}
                        active={isColumnHeader}
                      />
                    )}
                  </>
                )}
            </Box>
          </Box>
          {/* Color Selection Toolbar for text highlights */}
          {showHighlightColorToolbar && (
            <ColorSelectionToolbar
              onColorSelect={applyTextHighlightColor}
              onReset={clearTextHighlight}
              isDarkMode={isDarkMode}
            />
          )}
        </Box>
        {/* </Tooltip.Group> */}
      </ThemeContainer>
    </ThemeProvider>
  );
};
